/** Approval inbox, policies and delegations. Keys are flat, dot-namespaced and stable; placeholders use {name}. */
export const enBoApprovals = {
  // Inbox and history
  'bo.approvals.title': 'Approvals',
  'bo.approvals.subtitle': 'Decisions waiting for you in {branch}, and what was decided.',
  'bo.approvals.tabs': 'Approval views',
  'bo.approvals.tab.inbox': 'Inbox',
  'bo.approvals.tab.history': 'History',
  'bo.approvals.myDelegations': 'My delegations',
  'bo.approvals.refreshing': 'Refreshing…',
  'bo.approvals.unknownUser': 'Unknown',
  'bo.approvals.byAt': '{name} · {time}',
  'bo.approvals.inboxTitle': 'Waiting for your decision',
  'bo.approvals.inboxDesc': 'What you may decide now in this branch, oldest first.',
  'bo.approvals.inboxEmpty':
    'Nothing is waiting for you. Purchase requests, purchase orders, waste records and stock counts appear here when a step needs your decision.',
  'bo.approvals.inStok': 'Decided in Stock',
  'bo.approvals.col.type': 'Type',
  'bo.approvals.col.document': 'Document',
  'bo.approvals.col.step': 'Step',
  'bo.approvals.col.submitted': 'Submitted',
  'bo.approvals.col.submittedBy': 'Submitted by',
  'bo.approvals.col.status': 'Status',
  'bo.approvals.col.decided': 'Decided',
  'bo.approvals.historyTitle': 'Decided requests',
  'bo.approvals.historyDesc': 'The latest approval requests of this branch, newest first.',
  'bo.approvals.historyFilter': 'Filter by status',
  'bo.approvals.filter.decided': 'All decided',
  'bo.approvals.historyEmpty':
    'No decided requests yet. Approved, rejected and cancelled requests appear here.',
  'bo.approvals.historyEmptyFiltered': 'No requests with this status yet.',
  // Detail drawer
  'bo.approvals.drawer.title': 'Approval request',
  'bo.approvals.drawer.status': 'Status',
  'bo.approvals.drawer.policy': 'Policy',
  'bo.approvals.drawer.policyValue': '{name} · version {version}',
  'bo.approvals.drawer.submitted': 'Submitted',
  'bo.approvals.drawer.decided': 'Decided',
  'bo.approvals.drawer.openDocument': 'Open the document',
  'bo.approvals.drawer.readOnly':
    'This request is decided; its steps are kept as a record and cannot change.',
  // Escalation
  'bo.approvals.escalate.title': 'Escalate',
  'bo.approvals.escalate.desc':
    'Hand the current step ({step}) to another person, e.g. when the approver is away. Only that person can then decide it.',
  'bo.approvals.escalate.open': 'Escalate…',
  'bo.approvals.escalate.to': 'Hand to',
  'bo.approvals.escalate.toHint':
    'Only people allowed to decide this step are listed — never the requester or anyone who already approved.',
  'bo.approvals.escalate.choose': 'Choose an approver',
  'bo.approvals.escalate.toRequired': 'Choose who decides this step.',
  'bo.approvals.escalate.reason': 'Reason',
  'bo.approvals.escalate.reasonHint': 'Shown on the step, e.g. “Manager on leave”.',
  'bo.approvals.escalate.reasonShort': 'Write a reason of at least 3 characters.',
  'bo.approvals.escalate.submit': 'Escalate',
  'bo.approvals.escalate.submitting': 'Escalating…',
  'bo.approvals.escalate.done': 'Step handed to {name}.',
  'bo.approvals.escalate.noStaff':
    'Nobody else in this branch may decide this step. Give someone the permission first.',

  // Policies
  'bo.policies.title': 'Approval policies',
  'bo.policies.subtitle': 'Who must approve what, by document type, branch and amount.',
  'bo.policies.tabs': 'Approval settings',
  'bo.policies.tab.policies': 'Policies',
  'bo.policies.tab.delegations': 'Delegations',
  'bo.policies.add': 'Add policy',
  'bo.policies.readOnly': 'Only people allowed to manage approvals can see and change policies.',
  'bo.policies.noAccess':
    'Policies decide who approves purchase requests, orders, supplier invoices and payments. Ask a manager with approval rights to change them.',
  'bo.policies.howChosen':
    'A submission uses the active policy of its type whose amount band contains the amount. A branch policy wins over an all-branches one. Without a matching policy, one default step applies (purchasing, finance approval or payment verification) — never an automatic approval.',
  'bo.policies.empty':
    'No policies yet, so documents are approved as soon as they are submitted. Add a policy per document type and amount band.',
  'bo.policies.domainEmpty':
    'No policy for this type: submissions get the default single approval step.',
  'bo.policies.domainCount': 'Policies: {count}',
  'bo.policies.col.name': 'Name',
  'bo.policies.col.branch': 'Branch',
  'bo.policies.col.band': 'Amount band',
  'bo.policies.steps': 'Steps',
  'bo.policies.version': 'Version {version}',
  'bo.policies.allBranches': 'All branches',
  'bo.policies.otherBranch': 'Another branch',
  'bo.policies.band.atLeast': 'From',
  'bo.policies.band.below': 'below',
  'bo.policies.editNamed': 'Edit {name}',
  'bo.policies.createTitle': 'New approval policy',
  'bo.policies.editTitle': 'Edit approval policy',
  'bo.policies.formDesc': 'Steps are decided in order, each by a different person.',
  'bo.policies.snapshotNote':
    'Changes apply to new submissions only. Requests already waiting keep the steps they were submitted with.',
  'bo.policies.domain': 'Document type',
  'bo.policies.domainHint': 'Cannot be changed after the policy is created.',
  'bo.policies.nameHint': 'For example “Purchases above 5 million”.',
  'bo.policies.branchHint': 'All branches, or only this one (a branch policy wins).',
  'bo.policies.min': 'From amount ({currency})',
  'bo.policies.minHint': 'Included.',
  'bo.policies.max': 'Below amount ({currency})',
  'bo.policies.maxHint': 'Not included.',
  'bo.policies.maxHintOpen': 'Empty = no upper limit.',
  'bo.policies.amountInvalid': 'Enter a whole amount (digits only).',
  'bo.policies.maxAboveMin': 'The upper limit must be above the lower limit.',
  'bo.policies.stepsHint':
    '1 to {max} steps, decided in this order. Nobody can approve two steps of one request.',
  'bo.policies.stepsCount': 'A policy needs 1 to {max} steps.',
  'bo.policies.step.n': 'Step {n}',
  'bo.policies.step.label': 'Label',
  'bo.policies.step.labelPlaceholder': 'e.g. Manager check',
  'bo.policies.step.labelRequired': 'Give the step a label.',
  'bo.policies.step.permission': 'Permission needed',
  'bo.policies.step.permissionRequired': 'Choose a permission.',
  'bo.policies.step.role': 'Role',
  'bo.policies.step.anyRole': 'Any role',
  'bo.policies.step.up': 'Move step {n} up',
  'bo.policies.step.down': 'Move step {n} down',
  'bo.policies.step.remove': 'Remove step {n}',
  'bo.policies.step.add': 'Add step',
  'bo.policies.activeLabel': 'Active',
  'bo.policies.activeHint': 'Inactive policies are skipped for new submissions.',
  'bo.policies.saved': 'Policy “{name}” saved.',

  // Delegations
  'bo.delegations.title': 'Approval delegations',
  'bo.delegations.subtitle':
    'Let a colleague decide your approval steps while you are away. They decide on your behalf, and the record shows both names.',
  'bo.delegations.add': 'Delegate',
  'bo.delegations.mineTitle': 'My delegations',
  'bo.delegations.mineDesc': 'Delegations you gave or received.',
  'bo.delegations.allTitle': 'All delegations',
  'bo.delegations.allDesc':
    'You see everyone’s delegations because you may manage approvals; you can revoke any of them.',
  'bo.delegations.empty':
    'No delegations yet. Delegate before you go on leave so requests are not held up.',
  'bo.delegations.you': 'You',
  'bo.delegations.col.delegator': 'From',
  'bo.delegations.delegate': 'Delegate',
  'bo.delegations.col.branch': 'Branch',
  'bo.delegations.col.period': 'Valid',
  'bo.delegations.col.status': 'Status',
  'bo.delegations.reason': 'Reason',
  'bo.delegations.fromAt': 'from {time}',
  'bo.delegations.toAt': 'until {time}',
  'bo.delegations.state.active': 'Active',
  'bo.delegations.state.scheduled': 'Scheduled',
  'bo.delegations.state.expired': 'Expired',
  'bo.delegations.state.revoked': 'Revoked',
  'bo.delegations.revoke': 'Revoke',
  'bo.delegations.revokeNamed': 'Revoke delegation to {name}',
  'bo.delegations.revokeTitle': 'Revoke delegation?',
  'bo.delegations.revokeBody':
    '{name} can no longer decide approvals under this delegation. Decisions already made stay.',
  'bo.delegations.revoking': 'Revoking…',
  'bo.delegations.revoked': 'Delegation revoked.',
  'bo.delegations.createTitle': 'Delegate my approvals',
  'bo.delegations.formDesc':
    'The delegate can decide the steps you could decide, only within this period.',
  'bo.delegations.delegateHint': 'An active member of your team other than you.',
  'bo.delegations.choose': 'Choose a colleague',
  'bo.delegations.delegateRequired': 'Choose who decides for you.',
  'bo.delegations.branchHint': 'All your branches, or only one.',
  'bo.delegations.from': 'Valid from',
  'bo.delegations.to': 'Valid until',
  'bo.delegations.timeZoneHint': 'Branch time ({zone}).',
  'bo.delegations.timeRequired': 'Enter a date and time.',
  'bo.delegations.timeInvalid': 'Enter a valid date and time.',
  'bo.delegations.toAfterFrom': 'The end must be after the start.',
  'bo.delegations.reasonHint': 'For example “Annual leave”.',
  'bo.delegations.reasonShort': 'Write a reason of at least 3 characters.',
  'bo.delegations.submit': 'Delegate',
  'bo.delegations.created': 'Delegation saved.',
  'bo.delegations.noStaff': 'There is no other active team member to delegate to.',
} as const;
